import { addYears } from 'date-fns';
import { serviceModel } from '~/models';
import { DiscountPeriodExpirationModel } from '~/models/DiscountPeriodExpirationModel';
import { DiscountExpirationType, LimitedTimeDiscountOfferModel } from '~/models/LimitedTimeDiscountOfferModel';
import { PriceIncreaseModel } from '~/models/PriceIncreaseModel';
import { DEFAULT_DISCOUNT_DEADLINE_DAYS, DEFAULT_DISCOUNT_EXPIRATION_YEARS, DEFAULT_LIMIT_BILLING_TIMES, DEFAULT_OPTIONAL_INCLUDED_BY_DEFAULT, DEFAULT_PRACTICE_MANAGEMENT_ENABLED, DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING, DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS, DEFAULT_PRICE_INCREASE_DATE, DEFAULT_PRICE_INCREASE_PERCENT, DEFAULT_PRICE_INCREASE_ROUND_PRICES, DEFAULT_QBO_CLASS_ID } from './AdvancedSettingDefaults';
import { AdvancedSetting, IncreaseMethod } from './ServiceAdvancedSettings.types';
import { getInitialIncreaseMethod, getInitialStartBillingAtDate, getValidIncreaseOn } from './ServiceAdvancedSettings.utils';
/**
 * Enables an advanced setting with its default values by calling the appropriate handler.
 */ export function enableAdvancedSetting(type, ctx, handlers) {
    const serviceId = ctx.service.id;
    switch(type){
        case AdvancedSetting.Pause:
            var _handlers_onPausedChange;
            (_handlers_onPausedChange = handlers.onPausedChange) === null || _handlers_onPausedChange === void 0 ? void 0 : _handlers_onPausedChange.call(handlers, serviceId, true);
            return;
        case AdvancedSetting.BillOnApproval:
            var _handlers_onBillOnAcceptanceChange;
            (_handlers_onBillOnAcceptanceChange = handlers.onBillOnAcceptanceChange) === null || _handlers_onBillOnAcceptanceChange === void 0 ? void 0 : _handlers_onBillOnAcceptanceChange.call(handlers, serviceId, true);
            return;
        case AdvancedSetting.Prorate:
            var _handlers_onProratedChange;
            (_handlers_onProratedChange = handlers.onProratedChange) === null || _handlers_onProratedChange === void 0 ? void 0 : _handlers_onProratedChange.call(handlers, serviceId, true);
            return;
        case AdvancedSetting.Optional:
            var _handlers_onOptionalChange;
            (_handlers_onOptionalChange = handlers.onOptionalChange) === null || _handlers_onOptionalChange === void 0 ? void 0 : _handlers_onOptionalChange.call(handlers, serviceId, true, DEFAULT_OPTIONAL_INCLUDED_BY_DEFAULT);
            return;
        case AdvancedSetting.LimitCharges:
            var _handlers_onLimitBillingTimesChange;
            (_handlers_onLimitBillingTimesChange = handlers.onLimitBillingTimesChange) === null || _handlers_onLimitBillingTimesChange === void 0 ? void 0 : _handlers_onLimitBillingTimesChange.call(handlers, serviceId, true, ctx.SERVICE_SETTINGS_DROPDOWN ? DEFAULT_LIMIT_BILLING_TIMES : null);
            return;
        case AdvancedSetting.AutoPriceIncrease:
            {
                var _handlers_onAutoPriceIncreaseChange;
                const increaseOn = getValidIncreaseOn(DEFAULT_PRICE_INCREASE_DATE(ctx.now), ctx.now);
                const increaseMethod = getInitialIncreaseMethod(PriceIncreaseModel.makeNone(), ctx.relationshipState);
                const priceIncrease = PriceIncreaseModel.make(DEFAULT_PRICE_INCREASE_ROUND_PRICES, DEFAULT_PRICE_INCREASE_PERCENT, increaseMethod === IncreaseMethod.Date ? increaseOn : null);
                (_handlers_onAutoPriceIncreaseChange = handlers.onAutoPriceIncreaseChange) === null || _handlers_onAutoPriceIncreaseChange === void 0 ? void 0 : _handlers_onAutoPriceIncreaseChange.call(handlers, serviceId, priceIncrease);
                return;
            }
        case AdvancedSetting.QboClasses:
            var _handlers_onQboClassChange;
            (_handlers_onQboClassChange = handlers.onQboClassChange) === null || _handlers_onQboClassChange === void 0 ? void 0 : _handlers_onQboClassChange.call(handlers, serviceId, serviceModel.makeIntegratedClassSet(DEFAULT_QBO_CLASS_ID));
            return;
        case AdvancedSetting.FinancialCents:
            var _handlers_onFinancialCentsChange;
            (_handlers_onFinancialCentsChange = handlers.onFinancialCentsChange) === null || _handlers_onFinancialCentsChange === void 0 ? void 0 : _handlers_onFinancialCentsChange.call(handlers, serviceId, {
                teamMembers: DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS,
                enabled: DEFAULT_PRACTICE_MANAGEMENT_ENABLED
            }, {
                preserveExistingTeamMembers: DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING
            });
            return;
        case AdvancedSetting.Karbon:
            var _handlers_onKarbonChange;
            (_handlers_onKarbonChange = handlers.onKarbonChange) === null || _handlers_onKarbonChange === void 0 ? void 0 : _handlers_onKarbonChange.call(handlers, serviceId, {
                teamMembers: DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS,
                enabled: DEFAULT_PRACTICE_MANAGEMENT_ENABLED
            }, {
                preserveExistingTeamMembers: DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING
            });
            return;
        case AdvancedSetting.LimitedTimeDiscountOffer:
            var _handlers_onLimitedTimeDiscountOfferChange;
            (_handlers_onLimitedTimeDiscountOfferChange = handlers.onLimitedTimeDiscountOfferChange) === null || _handlers_onLimitedTimeDiscountOfferChange === void 0 ? void 0 : _handlers_onLimitedTimeDiscountOfferChange.call(handlers, serviceId, new LimitedTimeDiscountOfferModel({
                type: DiscountExpirationType.Days,
                days: DEFAULT_DISCOUNT_DEADLINE_DAYS
            }));
            return;
        case AdvancedSetting.LimitedTimeDiscountOfferForAgreement:
            var _handlers_onDiscountPeriodExpirationChange;
            (_handlers_onDiscountPeriodExpirationChange = handlers.onDiscountPeriodExpirationChange) === null || _handlers_onDiscountPeriodExpirationChange === void 0 ? void 0 : _handlers_onDiscountPeriodExpirationChange.call(handlers, serviceId, ctx.relationshipState.type === 'approved' ? DiscountPeriodExpirationModel.makeDate(addYears(ctx.now, 1)) : DiscountPeriodExpirationModel.makePeriod('years', DEFAULT_DISCOUNT_EXPIRATION_YEARS));
            return;
        case AdvancedSetting.PreapprovedAmount:
            var _handlers_onPreapprovedAmountChange;
            (_handlers_onPreapprovedAmountChange = handlers.onPreapprovedAmountChange) === null || _handlers_onPreapprovedAmountChange === void 0 ? void 0 : _handlers_onPreapprovedAmountChange.call(handlers, serviceId, null, true);
            return;
        case AdvancedSetting.StartAutomaticBillingAt:
            {
                var _handlers_onStartBillingAtChange;
                const startBillingAt = getInitialStartBillingAtDate({
                    now: ctx.now,
                    relationshipEffectiveDate: ctx.relationshipState.effectiveDate,
                    isAmendedService: serviceModel.isFromAmendment(ctx.service)
                });
                (_handlers_onStartBillingAtChange = handlers.onStartBillingAtChange) === null || _handlers_onStartBillingAtChange === void 0 ? void 0 : _handlers_onStartBillingAtChange.call(handlers, serviceId, startBillingAt);
                return;
            }
    }
}
